import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import {
  Loader2, Play, Eye, CheckCircle, XCircle, AlertCircle, Shield,
  ExternalLink, Webhook, Copy, Check, Trash2, RefreshCw, Link, Target,
  BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import Header from "@/components/layout/Header";

function ResultCard({ summary }) {
  if (!summary) return null;
  const dryOrders = summary.dry_run_orders || [];
  const hasError = summary.errors?.length > 0;
  return (
    <Card className="border-border bg-surface-2">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          {summary.dry_run ? (
            <><Eye className="w-4 h-4 text-muted-foreground" /> Dry Run Result</>
          ) : (
            <><CheckCircle className="w-4 h-4 text-gain" /> Execution Result</>
          )}
        </CardTitle>
        <CardDescription>
          {summary.dry_run
            ? "This is a preview — no order was placed."
            : `${summary.orders_placed} order(s) placed`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Underlying LTP", value: summary.underlying_ltp != null ? `₹${summary.underlying_ltp}` : "—" },
            { label: "Strike", value: summary.selected_strike || "—" },
            { label: "Expiry", value: summary.expiry || "—" },
            { label: "Lot Size", value: summary.lot_size || "—" },
          ].map((item) => (
            <div key={item.label} className="bg-surface-1 rounded-sm p-3 border border-border">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{item.label}</div>
              <div className="text-lg font-mono font-medium mt-0.5">{item.value}</div>
            </div>
          ))}
        </div>

        {dryOrders.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Orders</div>
            {dryOrders.map((o, i) => (
              <div key={i} className="bg-surface-1 rounded-sm p-3 border border-border text-sm space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] font-mono border-border">{o.symbol}</Badge>
                  <Badge className={`text-[10px] ${o.transaction_type === "BUY" ? "bg-gain/20 text-gain" : "bg-loss/20 text-loss"}`}>
                    {o.transaction_type}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] font-mono border-border">
                    {o.order_type === "SL-M" ? "SL-M" : o.order_type}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] font-mono border-border">Qty: {o.quantity}</Badge>
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                  <span>Sec ID: {o.security_id}</span>
                  <span>{o.exchange_segment}</span>
                  <span>{o.product}</span>
                  {o.strike && <span>Strike: {o.strike}</span>}
                  {o.option_type && <span>Type: {o.option_type}</span>}
                  {o.expiry && <span>Expiry: {o.expiry}</span>}
                  {o.lot_size && <span>Lot: {o.lot_size}</span>}
                  {o.estimated_premium != null && <span>Est. Premium: ₹{o.estimated_premium}</span>}
                  {o.trigger_price > 0 && <span>SL @ ₹{o.trigger_price}</span>}
                  {o.price > 0 && <span>Target @ ₹{o.price}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {hasError && (
          <div className="bg-loss/10 border border-loss/30 rounded-sm p-3">
            <div className="text-xs font-medium text-loss flex items-center gap-1.5 mb-1">
              <XCircle className="w-3.5 h-3.5" /> Errors
            </div>
            {summary.errors.map((e, i) => (
              <p key={i} className="text-xs text-loss/80">{e.symbol}: {e.message}</p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PositionsMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const res = await api.get("/option-trading/positions");
      setData(res.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPositions(); }, []);

  return (
    <Card className="border-border bg-surface-2" data-testid="option-positions-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
              Open Option Positions
            </CardTitle>
            <CardDescription>
              Live F&O option positions across all connected brokers
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchPositions} disabled={loading} className="h-7 w-7 p-0 rounded-sm">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-4">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading positions...
          </div>
        )}

        {!loading && data && data.positions?.length === 0 && (
          <div className="text-xs text-muted-foreground py-4 text-center">
            No open F&O positions. Trade signals from Chartink will appear here.
          </div>
        )}

        {!loading && data && data.positions?.length > 0 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-surface-1 rounded-sm p-3 border border-border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Invested</div>
                <div className="text-lg font-mono font-medium mt-0.5" data-testid="positions-invested">₹{data.totals.total_invested.toLocaleString()}</div>
              </div>
              <div className="bg-surface-1 rounded-sm p-3 border border-border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current Value</div>
                <div className="text-lg font-mono font-medium mt-0.5">₹{data.totals.total_current_value.toLocaleString()}</div>
              </div>
              <div className="bg-surface-1 rounded-sm p-3 border border-border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">P&L</div>
                <div className={`text-lg font-mono font-medium mt-0.5 ${data.totals.total_pnl >= 0 ? "text-gain" : "text-loss"}`} data-testid="positions-pnl">
                  ₹{data.totals.total_pnl.toLocaleString()} ({data.totals.total_pnl_pct > 0 ? "+" : ""}{data.totals.total_pnl_pct}%)
                </div>
              </div>
              <div className="bg-surface-1 rounded-sm p-3 border border-border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Open Positions</div>
                <div className="text-lg font-mono font-medium mt-0.5">{data.totals.open_positions}</div>
              </div>
            </div>

            <div className="space-y-1.5">
              {data.positions.map((pos, i) => (
                <div key={i} className="bg-surface-1 rounded-sm p-3 border border-border text-sm" data-testid={`option-position-${i}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] font-mono border-border">{pos.symbol}</Badge>
                      <Badge className={`text-[10px] ${pos.quantity > 0 ? "bg-gain/20 text-gain" : "bg-loss/20 text-loss"}`}>
                        {pos.quantity > 0 ? "LONG" : "SHORT"} {Math.abs(pos.quantity)}
                      </Badge>
                    </div>
                    <span className={`text-xs font-mono ${pos.pnl >= 0 ? "text-gain" : "text-loss"}`}>
                      {pos.pnl >= 0 ? "+" : ""}₹{pos.pnl.toLocaleString()} ({pos.pnl_pct > 0 ? "+" : ""}{pos.pnl_pct}%)
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-0.5">
                    <span>Entry: ₹{pos.avg_price}</span>
                    <span>LTP: {pos.ltp != null ? `₹${pos.ltp}` : "—"}</span>
                    <span>Product: {pos.product}</span>
                    <span>{pos.exchange_segment}</span>
                  </div>
                </div>
              ))}
            </div>

            {data.active_orders?.length > 0 && (
              <div className="bg-surface-1 rounded-sm p-3 border border-border">
                <div className="text-xs font-medium text-muted-foreground mb-2">Active SL / Target Orders</div>
                {data.active_orders.map((o, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                    <span>{o.symbol}</span>
                    <Badge variant="outline" className="text-[10px] border-border">{o.order_type}</Badge>
                    <span>{o.transaction_type}</span>
                    <span>Trigger: ₹{o.trigger_price}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function WebhookSetupCard({ onCreate, onRefresh }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [webhookName, setWebhookName] = useState("Chartink Options");
  const [sName, setSName] = useState("Option Buy Strategy");
  const [copiedUrl, setCopiedUrl] = useState(null);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const res = await api.get("/webhooks/sources");
      setSources(res.data?.sources || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSources(); }, []);

  const handleCreate = async () => {
    if (!webhookName.trim()) return;
    setCreating(true);
    try {
      const srcRes = await api.post("/webhooks/sources", {
        name: webhookName.trim(),
        provider: "chartink",
        strategy_ids: [],
        enabled: true,
      });
      const sourceId = srcRes.data.id;
      const webhookUrl = srcRes.data.webhook_url;

      await api.post("/strategies", {
        name: sName.trim(),
        algo_type: "intraday_option_buy",
        webhook_source_id: sourceId,
        algo_config: {
          fixed_lots: 1,
          option_type: "PE",
          premium_pct: 1.5,
          default_product: "MIS",
          default_exchange_segment: "NSE_FNO",
          dry_run: true,
          stop_loss_pct: 75,
          target_pct: 5,
        },
        entry_rules: { side_detection: "alert_name" },
        enabled: true,
      });

      toast.success("Webhook source + strategy created");

      try {
        await navigator.clipboard.writeText(webhookUrl);
        toast.success("Webhook URL copied to clipboard");
      } catch (_) {}

      await fetchSources();
      if (onCreate) onCreate(webhookUrl, sourceId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create webhook");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (source) => {
    setDeleting(source.id);
    try {
      await api.delete(`/webhooks/sources/${source.id}`);
      toast.success("Webhook source deleted");
      await fetchSources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      toast.success("URL copied");
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (_) {}
  };

  return (
    <Card className="border-border bg-surface-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <Webhook className="w-4 h-4 text-muted-foreground" />
              Webhook Setup
            </CardTitle>
            <CardDescription>
              Create a webhook source and paste the URL into Chartink to receive signals
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchSources} disabled={loading} className="h-7 w-7 p-0 rounded-sm">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {sources.length === 0 && !loading && (
          <div className="bg-surface-1 rounded-sm p-4 border border-border space-y-3">
            <p className="text-xs text-muted-foreground">
              No webhook sources yet. Create one to get a URL for Chartink:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Webhook Name</Label>
                <Input
                  value={webhookName}
                  onChange={(e) => setWebhookName(e.target.value)}
                  className="rounded-sm bg-surface-2 border-border text-sm h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Strategy Name</Label>
                <Input
                  value={sName}
                  onChange={(e) => setSName(e.target.value)}
                  className="rounded-sm bg-surface-2 border-border text-sm h-8"
                />
              </div>
            </div>
            <Button onClick={handleCreate} disabled={creating || !webhookName.trim()} size="sm" className="rounded-sm">
              {creating && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
              <Link className="w-3 h-3 mr-1.5" />
              Create Webhook + Strategy
            </Button>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading webhook sources...
          </div>
        )}

        {sources.map((src) => {
          const url = src.webhook_url || src.url || "";
          return (
            <div key={src.id} className="bg-surface-1 rounded-sm p-3 border border-border space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] border-border">{src.name}</Badge>
                  <Badge className={`text-[10px] ${src.enabled ? "bg-gain/20 text-gain" : "bg-loss/20 text-loss"}`}>
                    {src.enabled ? "Active" : "Disabled"}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(src)}
                  disabled={deleting === src.id}
                  className="h-6 w-6 p-0 rounded-sm text-loss/60 hover:text-loss"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono bg-surface-2 rounded-sm px-2 py-1.5 border border-border truncate">
                  {url || "—"}
                </code>
                {url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyUrl(url)}
                    className="h-7 w-7 p-0 rounded-sm shrink-0"
                    title="Copy URL"
                  >
                    {copiedUrl === url ? <Check className="w-3.5 h-3.5 text-gain" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                )}
              </div>
              {src.strategy_count > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  {src.strategy_count} linked strategy(ies)
                </p>
              )}
            </div>
          );
        })}

        {sources.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            Paste the URL above as a <strong>Webhook URL</strong> in your Chartink alert. 
            Chartink will send a POST with <code className="text-[10px] font-mono">stocks</code> and <code className="text-[10px] font-mono">alert_name</code> fields.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function OptionTrading({ user }) {
  const [symbol, setSymbol] = useState("");
  const [optionType, setOptionType] = useState("PE");
  const [fixedLots, setFixedLots] = useState(1);
  const [premiumPct, setPremiumPct] = useState(1.5);
  const [stopLossPct, setStopLossPct] = useState(75);
  const [targetPct, setTargetPct] = useState(5);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleExecute = async () => {
    if (!symbol.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await api.post("/option-trading/execute", {
        symbol: symbol.trim().toUpperCase(),
        option_type: optionType,
        fixed_lots: fixedLots,
        premium_pct: premiumPct,
        stop_loss_pct: stopLossPct,
        target_pct: targetPct,
        dry_run: dryRun,
      });
      setResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-1 text-foreground">
      <Header user={user} />
      <main className="max-w-[1200px] mx-auto px-6 py-6 space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            / Investment Pro
          </div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight mt-1">
            Option Trading
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Intraday ATM option buying via Chartink webhook signals or manual execution
          </p>
        </div>

        <WebhookSetupCard />

        <Separator className="bg-border" />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-2 border-border bg-surface-2 h-fit">
            <CardHeader>
              <CardTitle className="text-sm">Manual Trade</CardTitle>
              <CardDescription>One-shot execution for testing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="symbol">Underlying Symbol</Label>
                <Input
                  id="symbol"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  placeholder="e.g. BANKNIFTY, NIFTY, RELIANCE"
                  className="rounded-sm bg-surface-1 border-border font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="option_type">Option Type</Label>
                <Select value={optionType} onValueChange={setOptionType}>
                  <SelectTrigger id="option_type" className="rounded-sm bg-surface-1 border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface-2 border-border">
                    <SelectItem value="PE">Put (PE)</SelectItem>
                    <SelectItem value="CE">Call (CE)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fixed_lots">Fixed Lots</Label>
                <Input
                  id="fixed_lots"
                  type="number"
                  min={1}
                  max={100}
                  value={fixedLots}
                  onChange={(e) => setFixedLots(parseInt(e.target.value) || 1)}
                  className="rounded-sm bg-surface-1 border-border font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="premium_pct">Premium % of LTP</Label>
                <Input
                  id="premium_pct"
                  type="number"
                  step={0.1}
                  min={0.1}
                  max={10}
                  value={premiumPct}
                  onChange={(e) => setPremiumPct(parseFloat(e.target.value) || 1.5)}
                  className="rounded-sm bg-surface-1 border-border font-mono"
                />
              </div>

              <Separator className="bg-border" />

              <div className="space-y-3">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Shield className="w-3 h-3" /> Exit Settings
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="stop_loss_pct" className="text-xs">Stop Loss %</Label>
                    <Input
                      id="stop_loss_pct"
                      type="number"
                      min={1}
                      max={99}
                      value={stopLossPct}
                      onChange={(e) => setStopLossPct(parseFloat(e.target.value) || 75)}
                      className="rounded-sm bg-surface-1 border-border font-mono h-8"
                    />
                    <p className="text-[10px] text-muted-foreground">Exit if premium drops X%</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="target_pct" className="text-xs">Target %</Label>
                    <Input
                      id="target_pct"
                      type="number"
                      step={0.1}
                      min={0.1}
                      value={targetPct}
                      onChange={(e) => setTargetPct(parseFloat(e.target.value) || 5)}
                      className="rounded-sm bg-surface-1 border-border font-mono h-8"
                    />
                    <p className="text-[10px] text-muted-foreground">Book profit at X% gain</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border border-border rounded-sm p-3 bg-surface-1">
                <div>
                  <Label htmlFor="dry_run" className="cursor-pointer text-sm">Dry Run</Label>
                  <p className="text-[11px] text-muted-foreground">Preview without placing order</p>
                </div>
                <Switch id="dry_run" checked={dryRun} onCheckedChange={setDryRun} />
              </div>

              <Button
                onClick={handleExecute}
                disabled={loading || !symbol.trim()}
                className="w-full rounded-sm"
                size="lg"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {dryRun ? (
                  <><Eye className="w-4 h-4 mr-2" /> Dry Run</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" /> Execute Trade</>
                )}
              </Button>
            </CardContent>
          </Card>

          <div className="lg:col-span-3 space-y-4">
            {error && (
              <Card className="border-loss/50 bg-loss/5">
                <CardContent className="p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-loss shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-loss">Execution Failed</p>
                    <p className="text-xs text-loss/80 mt-0.5">{error}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {!result && !error && (
              <Card className="border-border bg-surface-2 h-full">
                <CardContent className="p-12 flex flex-col items-center justify-center text-center">
                  <ExternalLink className="w-8 h-8 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Configure the trade parameters and click{" "}
                    <span className="font-medium text-foreground">
                      {dryRun ? "Dry Run" : "Execute Trade"}
                    </span>{" "}
                    to preview or place the order.
                  </p>
                </CardContent>
              </Card>
            )}

            {result && <ResultCard summary={result.summary} />}
          </div>
        </div>

        <Separator className="bg-border" />

        <PositionsMonitor />
      </main>
    </div>
  );
}
